// checkout/utils/loqate/createInstance.ts

import { toggleFormRow } from '../toggleFormRows';
import type { LoqateInstanceConfig, LoqateLookupItem, LoqateRetrieveResponse } from './types';
import { getContextPath, getFirstString, normaliseLookupItems } from './helpers';

/**
 * Create instance.
 * Multiple instances can exist on same page.
 *
 * Keyboard behaviour:
 * - Tab from the search input moves focus into the first result (when open).
 * - Tab / Shift+Tab cycle through results (wraps around).
 * - ArrowUp / ArrowDown also move through results (wraps around).
 * - Escape closes results and returns focus to the search input (when in results).
 *
 * @param {LoqateInstanceConfig} config Instance config
 * @returns {void}
 */
export function createLoqateInstance(config: LoqateInstanceConfig): void {
	const {
		searchInput,
		manualButton,
		isDelivery,
		limit,
		line1,
		line2,
		line3,
		town,
		county,
		postcode,
	} = config;

	console.log('[Loqate] createLoqateInstance()', {
		id: config.searchInput?.id,
		name: config.searchInput?.name,
		isDelivery: config.isDelivery,
		limit: config.limit,
		bound: config.searchInput?.dataset?.loqateBound,
	});

	// Prevent double-binding if markup is re-rendered but the input node persists.
	if (searchInput.dataset.loqateBound === 'true') return;
	searchInput.dataset.loqateBound = 'true';

	// Results container creation
	let resultsContainer: HTMLElement;

	if (config.resultsContainer) {
		resultsContainer = config.resultsContainer;
	} else {
		resultsContainer = document.createElement('div');
		resultsContainer.className = 'address-search__results';
		resultsContainer.setAttribute('role', 'listbox');
		resultsContainer.setAttribute('aria-label', 'Address suggestions');

		// Positioning (absolute dropdown, relative parent)
		const parent = searchInput.parentElement;
		if (parent) {
			const style = window.getComputedStyle(parent);
			if (style.position === 'static') parent.style.position = 'relative';
			parent.appendChild(resultsContainer);
		} else {
			searchInput.insertAdjacentElement('afterend', resultsContainer);
		}
	}

	// Give the listbox an ID so the input can reference it.
	if (!resultsContainer.id) {
		resultsContainer.id = `loqate-results-${Math.random().toString(16).slice(2)}`;
	}
	searchInput.setAttribute('aria-controls', resultsContainer.id);

	// State
	let debounceTimer: number | undefined;
	let currentQuery = '';
	let currentContainerId: string | null = null;

	let lookupAbort: AbortController | null = null;
	let retrieveAbort: AbortController | null = null;

	let requestSeq = 0; // Used to ignore old responses

	hideResults();

	// Keep dropdown aligned if layout changes while it's open.
	const repositionIfOpen = (): void => {
		if (!resultsContainer.hasAttribute('hidden')) positionResults();
	};

	window.addEventListener('resize', repositionIfOpen, { passive: true });
	window.addEventListener('scroll', repositionIfOpen, { passive: true, capture: true });

	// Input handling
	searchInput.addEventListener('input', () => {
		const value = searchInput.value.trim();
		currentQuery = value;
		currentContainerId = null;

		console.log('[Loqate] input', { value });

		if (debounceTimer) window.clearTimeout(debounceTimer);

		if (value.length < 3) {
			hideResults();
			return;
		}

		debounceTimer = window.setTimeout(() => {
			void lookup(value, null);
		}, 300);
	});

	searchInput.addEventListener('keydown', (event: KeyboardEvent) => {
		if (resultsContainer.hasAttribute('hidden')) return;

		const items = getResultElements();
		if (!items.length) return;

		// ArrowDown move into results.
		if (event.key === 'ArrowDown' || event.key === 'Down') {
			event.preventDefault();
			items[0].focus();
			return;
		}

		// Tab move into results (instead of skipping them).
		if (event.key === 'Tab' && !event.shiftKey) {
			event.preventDefault();
			items[0].focus();
			return;
		}

		// Esc close results.
		if (event.key === 'Escape' || event.key === 'Esc') {
			event.preventDefault();
			hideResults();
			return;
		}
	});

	// Click anywhere else closes dropdown
	document.addEventListener('click', (event) => {
		const target = event.target as Node | null;
		if (!target) return;
		if (!resultsContainer.contains(target) && target !== searchInput) hideResults();
	});

	// Positioning
	/**
	 * Position the results box directly above or below the input
	 * with a small pixel gap based on viewport space.
	 *
	 * @returns {void}
	 */
	function positionResults(): void {
		const container = resultsContainer.parentElement as HTMLElement | null;
		if (!container) return;

		const gap = 4;

		const containerRect = container.getBoundingClientRect();
		const inputRect = searchInput.getBoundingClientRect();

		const spaceBelow = window.innerHeight - inputRect.bottom;
		const spaceAbove = inputRect.top;

		const dropdownHeight = resultsContainer.offsetHeight || 0;
		const useAbove = spaceBelow < 200 && spaceAbove > spaceBelow;

		resultsContainer.style.top = 'auto';
		resultsContainer.style.bottom = 'auto';

		resultsContainer.classList.remove(
			'address-search__results--above',
			'address-search__results--below',
		);

		if (useAbove) {
			const top = inputRect.top - containerRect.top - gap - dropdownHeight;
			resultsContainer.style.top = `${top}px`;
			resultsContainer.classList.add('address-search__results--above');
		} else {
			const top = inputRect.bottom - containerRect.top + gap;
			resultsContainer.style.top = `${top}px`;
			resultsContainer.classList.add('address-search__results--below');
		}
	}

	/**
	 * Perform an address lookup or container drill-down.
	 *
	 * @param {string} query The text to search for
	 * @param {string | null} containerId Optional container ID for drill-down
	 * @returns {Promise<void>}
	 */
	async function lookup(query: string, containerId: string | null): Promise<void> {
		try {
			// Cancel any in progress lookup and start a new request sequence.
			if (lookupAbort) lookupAbort.abort();
			lookupAbort = new AbortController();
			const mySeq = ++requestSeq;

			const params = new URLSearchParams();
			params.set('addressQuery', query);

			if (containerId) {
				params.set('container', containerId);
			} else {
				params.set('limit', String(limit));
			}

			if (isDelivery) params.set('isDelivery', 'true');

			const base = getContextPath();
			const url = `${base}/address/lookup?${params.toString()}`;

			console.log('[Loqate] lookup fetch', url);

			const res = await fetch(url, {
				method: 'GET',
				credentials: 'same-origin',
				headers: { Accept: 'application/json' },
				signal: lookupAbort.signal,
			});

			// Ignore old responses if something else started since.
			if (mySeq !== requestSeq) return;

			if (!res.ok) {
				console.log('[Loqate] lookup status', res.status);

				if (res.status === 400 || res.status === 404) {
					renderNoResults();
					return;
				}
				console.error('[Loqate] Lookup failed:', res.status, url);
				renderLookupUnavailable();
				return;
			}

			const data = (await res.json()) as unknown;
			const normalisedItems = normaliseLookupItems(data);

			if (!normalisedItems.length) {
				renderNoResults();
				return;
			}

			renderResults(normalisedItems);
		} catch (err) {
			// Abort is expected during fast typing.
			if (err instanceof DOMException && err.name === 'AbortError') return;

			console.error('[Loqate] Lookup error:', err);
			renderLookupUnavailable();
		}
	}

	/**
	 * Retrieve full address details and populate the form.
	 *
	 * @param {string} addressId The Loqate address ID to retrieve
	 * @returns {Promise<void>}
	 */
	async function retrieve(addressId: string): Promise<void> {
		try {
			if (retrieveAbort) retrieveAbort.abort();
			retrieveAbort = new AbortController();

			const params = new URLSearchParams();
			params.set('id', addressId);

			const base = getContextPath();
			const url = `${base}/address/retrieve?${params.toString()}`;

			const res = await fetch(url, {
				method: 'GET',
				credentials: 'same-origin',
				headers: { Accept: 'application/json' },
				signal: retrieveAbort.signal,
			});

			if (!res.ok) {
				console.error('[Loqate] Retrieve failed:', res.status, url);
				return;
			}

			const data = (await res.json()) as LoqateRetrieveResponse;

			populateAddress(data);
			clearSearch();
			hideResults();
			revealManualFields();
		} catch (err) {
			if (err instanceof DOMException && err.name === 'AbortError') return;
			console.error('[Loqate] Retrieve error:', err);
		}
	}

	// Form population
	/**
	 * Populate the existing address fields from a raw retrieve response.
	 *
	 * @param {LoqateRetrieveResponse} data The retrieved address data
	 * @returns {void}
	 */
	function populateAddress(data: LoqateRetrieveResponse): void {
		const obj = data as Record<string, unknown>;

		const line1Value = getFirstString(obj, [
			'Line1',
			'line1',
			'Address1',
			'address1',
			'addressLine1',
		]);
		const line2Value = getFirstString(obj, [
			'Line2',
			'line2',
			'Address2',
			'address2',
			'addressLine2',
		]);
		const line3Value = getFirstString(obj, [
			'Line3',
			'line3',
			'Address3',
			'address3',
			'addressLine3',
		]);
		const cityValue = getFirstString(obj, ['City', 'city', 'Town', 'town', 'Locality']);
		const provinceValue = getFirstString(obj, [
			'Province',
			'province',
			'County',
			'county',
			'Region',
		]);
		const postalCodeValue = getFirstString(obj, [
			'PostalCode',
			'Postcode',
			'postalCode',
			'postcode',
			'ZipCode',
			'zipCode',
		]);

		// MinimalAddress now includes "countryCode" (ISO2)
		// Keep fallbacks for earlier test shapes.
		const countryCodeValue = getFirstString(obj, ['countryCode', 'countryIso2', 'CountryIso2']);

		line1.value = line1Value;
		if (line2) line2.value = line2Value;
		if (line3) line3.value = line3Value;
		town.value = cityValue;
		if (county) county.value = provinceValue;
		postcode.value = postalCodeValue;

		// Billing/R&C: set country select/input if present in the same form
		if (countryCodeValue) {
			searchInput.dataset.loqateCountryCode = countryCodeValue;

			const form =
				searchInput.closest('form') ??
				line1.closest('form') ??
				searchInput.form ??
				null ??
				line1.form ??
				null;

			if (form) {
				const countrySelect =
					form.querySelector<HTMLSelectElement>('select[data-pca-country="true"]') ??
					form.querySelector<HTMLSelectElement>('#country-code') ??
					form.querySelector<HTMLSelectElement>('select[name$=".countryCode"]');

				if (countrySelect) {
					countrySelect.value = countryCodeValue;
					countrySelect.dispatchEvent(new Event('input', { bubbles: true }));
					countrySelect.dispatchEvent(new Event('change', { bubbles: true }));
				} else {
					const countryInput =
						form.querySelector<HTMLInputElement>('input[data-pca-country="true"]') ??
						form.querySelector<HTMLInputElement>('#country-code') ??
						form.querySelector<HTMLInputElement>('input[name$=".countryCode"]');

					if (countryInput) {
						countryInput.value = countryCodeValue;
						countryInput.dispatchEvent(new Event('input', { bubbles: true }));
						countryInput.dispatchEvent(new Event('change', { bubbles: true }));
					}
				}
			}
		}
	}

	/**
	 * Clear the search input after a successful retrieve.
	 *
	 * @returns {void}
	 */
	function clearSearch(): void {
		searchInput.value = '';
	}

	/**
	 * Reveal manual fields
	 *
	 * @returns {void}
	 */
	function revealManualFields(): void {
		if (manualButton) {
			if (
				(manualButton as HTMLElement).dataset &&
				(manualButton as HTMLElement).dataset.loqateRevealMode === 'click'
			) {
				manualButton.click();
				return;
			}

			toggleFormRow(manualButton);
		}
	}

	// Rendering + focus management
	/**
	 * Hide and clear results.
	 *
	 * @returns {void}
	 */
	function hideResults(): void {
		resultsContainer.innerHTML = '';
		resultsContainer.setAttribute('hidden', 'true');
	}

	/**
	 * Render the "no results" message.
	 *
	 * @returns {void}
	 */
	function renderNoResults(): void {
		resultsContainer.innerHTML = '';
		resultsContainer.removeAttribute('hidden');

		const div = document.createElement('div');
		div.className = 'address-search__result';
		div.setAttribute('role', 'option');
		div.setAttribute('aria-selected', 'false');
		div.tabIndex = -1;
		div.textContent = 'No matches found';

		resultsContainer.appendChild(div);

		positionResults();
	}

	/**
	 * Render the "lookup unavailable" message.
	 *
	 * @returns {void}
	 */
	function renderLookupUnavailable(): void {
		resultsContainer.innerHTML = '';
		resultsContainer.removeAttribute('hidden');

		const div = document.createElement('div');
		div.className = 'address-search__result';
		div.setAttribute('role', 'option');
		div.setAttribute('aria-selected', 'false');
		div.tabIndex = -1;
		div.textContent =
			'Address lookup is currently unavailable. Please enter your address manually.';

		resultsContainer.appendChild(div);

		positionResults();
	}

	/**
	 * Render the lookup results into the suggestions container.
	 *
	 * @param {LoqateLookupItem[]} items Array of lookup results
	 * @returns {void}
	 */
	function renderResults(items: LoqateLookupItem[]): void {
		resultsContainer.innerHTML = '';
		resultsContainer.removeAttribute('hidden');

		items.forEach((item, index) => {
			const div = document.createElement('div');
			div.className = 'address-search__result';

			if (item.Type !== 'Address') {
				div.classList.add('address-search__result--container');
			}

			div.setAttribute('role', 'option');
			div.setAttribute('data-index', String(index));
			div.setAttribute('data-id', item.Id);
			div.setAttribute('data-type', item.Type);
			div.setAttribute('aria-selected', 'false');

			// Make every option focusable
			div.tabIndex = 0;

			// Keep backend formatting in Text.
			div.innerHTML = `
				<span class="address-search__result-text">${item.Text}</span>
				${
					item.Description
						? `<span class="address-search__result-description">${item.Description}</span>`
						: ''
				}
			`;

			div.addEventListener('click', () => handleResultActivate(item));

			div.addEventListener('focus', () => {
				setAriaSelected(index);
			});

			div.addEventListener('keydown', (event: KeyboardEvent) => {
				// Tab cycles through results (wrap)
				if (event.key === 'Tab') {
					event.preventDefault();
					const direction = event.shiftKey ? -1 : 1;
					focusRelativeResult(div, direction);
					return;
				}

				if (event.key === 'Enter' || event.key === ' ') {
					event.preventDefault();
					handleResultActivate(item);
					return;
				}

				if (event.key === 'ArrowDown' || event.key === 'Down') {
					event.preventDefault();
					focusRelativeResult(div, 1);
					return;
				}

				if (event.key === 'ArrowUp' || event.key === 'Up') {
					event.preventDefault();
					focusRelativeResult(div, -1);
					return;
				}

				if (event.key === 'Escape' || event.key === 'Esc') {
					event.preventDefault();
					hideResults();
					searchInput.focus();
				}
			});

			resultsContainer.appendChild(div);
		});

		positionResults();
	}

	/**
	 * Return all result option elements in order.
	 *
	 * @returns {HTMLElement[]} result elements
	 */
	function getResultElements(): HTMLElement[] {
		return Array.from(
			resultsContainer.querySelectorAll<HTMLElement>('.address-search__result'),
		);
	}

	/**
	 * Update aria-selected so screen readers have a clear "current" option.
	 *
	 * @param {number} index selected index
	 * @returns {void}
	 */
	function setAriaSelected(index: number): void {
		const items = getResultElements();
		items.forEach((el, i) => {
			el.setAttribute('aria-selected', i === index ? 'true' : 'false');
		});
	}

	/**
	 * Focus next/previous result relative to current, looped.
	 *
	 * @param {HTMLElement} current currently focused element
	 * @param {number} delta +1 next, -1 previous
	 * @returns {void}
	 */
	function focusRelativeResult(current: HTMLElement, delta: number): void {
		const items = getResultElements();
		if (!items.length) return;

		const index = items.indexOf(current);
		const start = index === -1 ? 0 : index;

		const nextIndex = (((start + delta) % items.length) + items.length) % items.length;
		items[nextIndex].focus();
	}

	/**
	 * Handle slected (click, enter or space)
	 *
	 * @param {LoqateLookupItem} item Activated item
	 * @returns {void}
	 */
	function handleResultActivate(item: LoqateLookupItem): void {
		if (item.Type === 'Address') {
			void retrieve(item.Id);
		} else {
			currentContainerId = item.Id;
			void lookup(currentQuery, currentContainerId);
		}
	}
}
